import React, { useState } from 'react';
import DentalRiskWizardModal from '../components/DentalRiskWizardModal';
import { useDentalRiskReports } from '../hooks/useDentalRiskReports';
import RiskMetricsCards from '../components/risk/RiskMetricsCards';
import RiskFilterBar from '../components/risk/RiskFilterBar';
import RiskKanbanBoard from '../components/risk/RiskKanbanBoard';
import RiskDataTable from '../components/risk/RiskDataTable';
import RiskCaseDetailModal from '../components/risk/RiskCaseDetailModal';
import RiskImageLightbox from '../components/risk/RiskImageLightbox';
import RiskSqlMigrationModal from '../components/risk/RiskSqlMigrationModal';

/**
 * Modernized Dental Risk Report Page (Orchestrator Component)
 */
const DentalRiskReportPage = ({
  user = null,
  userRole = '',
  isMobile = false,
  onNavigate = null
}) => {
  const [activeView, setActiveView] = useState('kanban'); // 'kanban' | 'table'
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [isSqlModalOpen, setIsSqlModalOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const {
    filteredReports,
    isLoading,
    metrics,
    uniqueLocations,
    selectedCase,
    setSelectedCase,
    isSavingInvestigation,
    searchQuery,
    setSearchQuery,
    filterDomain,
    setFilterDomain,
    filterStatus,
    setFilterStatus,
    filterSeverityGroup,
    setFilterSeverityGroup,
    filterLocation,
    setFilterLocation,
    activeFiltersCount,
    clearFilters,
    handleSaveNewReport,
    handleUpdateInvestigation,
    handleQuickStatusMove,
    handleTriggerRecall,
    handleExportCSV
  } = useDentalRiskReports(user, onNavigate);

  const handleOpenLightbox = (img) => {
    setLightboxImage(img);
    setIsLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setIsLightboxOpen(false);
    setLightboxImage(null);
  };

  return (
    <div className="risk-page-container">
      {/* 🌟 Top Page Header */}
      <div className="risk-header-bar">
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '28px' }}>🛡️</span>
          <div>
            <h1 style={{ margin: 0, fontSize: isMobile ? '1.3em' : '1.6em', fontWeight: '800', color: '#0f172a' }}>
              ระบบจัดการความเสี่ยงทันตกรรม (Dental Risk Management)
            </h1>
            <p style={{ margin: '2px 0 0 0', fontSize: '0.85em', color: '#64748b' }}>
              รายงานอุบัติการณ์, ประเมินความรุนแรง (Level A-I / 1-5), วิเคราะห์สาเหตุรากเหง้า (RCA) และมาตรการป้องกันเชิงระบบ
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="risk-header-actions">
          <button type="button" onClick={() => setIsSqlModalOpen(true)} className="risk-btn risk-btn--secondary">
            <span>💾</span>
            <span>SQL Migration</span>
          </button>

          <button type="button" onClick={handleExportCSV} className="risk-btn risk-btn--success">
            <span>📥</span>
            <span>ส่งออก CSV</span>
          </button>

          {onNavigate && (
            <button
              type="button"
              onClick={() => onNavigate('dental-risk-portal')}
              className="risk-btn risk-btn--portal"
              title="เปิดหน้า Standalone Portal (risk.fangdent.com)"
            >
              <span>🌐</span>
              <span>หน้าพอร์ทัลด่วน (Portal)</span>
            </button>
          )}

          <button type="button" onClick={() => setIsWizardOpen(true)} className="risk-btn risk-btn--primary">
            <span style={{ fontSize: '1.2em' }}>✍️</span>
            <span>เขียนรายงานความเสี่ยง</span>
          </button>
        </div>
      </div>

      {/* 📊 Top Summary Metrics Cards */}
      <RiskMetricsCards
        metrics={metrics}
        filterStatus={filterStatus}
        setFilterStatus={setFilterStatus}
        filterSeverityGroup={filterSeverityGroup}
        setFilterSeverityGroup={setFilterSeverityGroup}
        isMobile={isMobile}
      />

      {/* 🔍 Filter Bar & View Mode Toggle */}
      <RiskFilterBar
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        filterDomain={filterDomain}
        setFilterDomain={setFilterDomain}
        filterStatus={filterStatus}
        setFilterStatus={setFilterStatus}
        filterSeverityGroup={filterSeverityGroup}
        setFilterSeverityGroup={setFilterSeverityGroup}
        filterLocation={filterLocation}
        setFilterLocation={setFilterLocation}
        uniqueLocations={uniqueLocations}
        activeView={activeView}
        setActiveView={setActiveView}
        activeFiltersCount={activeFiltersCount}
        clearFilters={clearFilters}
        isMobile={isMobile}
      />

      {/* Main Content: Loading, Empty State, Kanban or Table View */}
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#64748b' }}>
          <span
            className="spinner"
            style={{
              display: 'inline-block',
              width: '28px',
              height: '28px',
              border: '3px solid #0284c7',
              borderTopColor: 'transparent',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
              marginBottom: '12px'
            }}
          />
          <div>กำลังโหลดข้อมูลรายงานความเสี่ยง...</div>
        </div>
      ) : filteredReports.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            padding: '60px 20px',
            backgroundColor: '#ffffff',
            borderRadius: '16px',
            border: '1px dashed #cbd5e1',
            color: '#64748b'
          }}
        >
          <div style={{ fontSize: '40px', marginBottom: '10px' }}>🛡️</div>
          <h3 style={{ margin: '0 0 6px 0', color: '#1e293b' }}>
            ไม่พบรายการอุบัติการณ์ตามเงื่อนไขที่เลือก
          </h3>
          <p style={{ margin: '0 0 16px 0', fontSize: '0.9em' }}>
            ลองปรับเปลี่ยนคำค้นหา หรือกดปุ่ม <strong>"เขียนรายงานความเสี่ยง"</strong> เพื่อสร้างรายงานใหม่
          </p>
          {activeFiltersCount > 0 && (
            <button
              type="button"
              onClick={clearFilters}
              style={{
                padding: '6px 16px',
                borderRadius: '8px',
                backgroundColor: '#f1f5f9',
                color: '#0284c7',
                border: '1px solid #cbd5e1',
                fontSize: '0.86em',
                fontWeight: '700',
                cursor: 'pointer'
              }}
            >
              ล้างตัวกรองทั้งหมด
            </button>
          )}
        </div>
      ) : (
        <>
          {activeView === 'kanban' && (
            <RiskKanbanBoard
              reports={filteredReports}
              onSelectCase={setSelectedCase}
              onQuickStatusMove={handleQuickStatusMove}
              isMobile={isMobile}
            />
          )}

          {activeView === 'table' && (
            <RiskDataTable
              reports={filteredReports}
              onSelectCase={setSelectedCase}
              isMobile={isMobile}
            />
          )}
        </>
      )}

      {/* Case Detail & Investigation Modal */}
      <RiskCaseDetailModal
        isOpen={!!selectedCase}
        caseItem={selectedCase}
        onClose={() => setSelectedCase(null)}
        onUpdateInvestigation={handleUpdateInvestigation}
        onTriggerRecall={(c) => handleTriggerRecall(c)}
        onOpenLightbox={handleOpenLightbox}
        isSavingInvestigation={isSavingInvestigation}
        currentUser={user}
        userRole={userRole}
        isMobile={isMobile}
      />

      {/* SQL Migration Modal */}
      <RiskSqlMigrationModal
        isOpen={isSqlModalOpen}
        onClose={() => setIsSqlModalOpen(false)}
      />

      {/* Lightbox Zoom Modal (with 4K NAS Toggle) */}
      <RiskImageLightbox
        isOpen={isLightboxOpen}
        imageItem={lightboxImage}
        onClose={handleCloseLightbox}
      />

      {/* Dental Risk Wizard Modal */}
      <DentalRiskWizardModal
        isOpen={isWizardOpen}
        onClose={() => setIsWizardOpen(false)}
        onSaveReport={handleSaveNewReport}
        currentUser={user}
        userRole={userRole}
      />
    </div>
  );
};

export default DentalRiskReportPage;
